import React, {
  ComponentProps,
  memo,
  useCallback,
  useRef,
  type ReactElement,
} from 'react';
import {
  Box,
  Button,
  HeadingBar,
  HeadingText,
  Modal,
  ParagraphText,
  StackLayout,
  useThemeStrings,
  VisuallyHidden,
} from '@leagueplatform/genesis-core';

import { MasonryShowModalMessage } from '#types/masonry-engine-message.types.ts';
import { HIDDEN_SUBTREE_CLASS_NAME } from '#constants/constants.ts';
import { MasonryEngineNode } from '../types/masonry-engine-node.types';
import { useMasonryEngineConfig } from '../masonry-engine-config-context';
import { useStateControllerStore } from '../masonry-engine-state-controller';

/**
 * Given the `layout` payload property of a `MasonryShowModalMessage`,
 * builds the appropriate `layout` prop value for the `Modal.Content` component.
 */
const buildLayoutPropValue = (
  layout: MasonryShowModalMessage['payload']['layout'],
): ComponentProps<typeof Modal.Content>['layout'] => {
  if (!layout || (!layout.web && !layout.mobileWeb)) {
    return 'standard';
  }

  const { web, mobileWeb } = layout;

  if (web && mobileWeb) {
    if (web === mobileWeb) {
      return web;
    }
    return { '@initial': web, '@mobile': mobileWeb };
  }

  if (web) {
    return web === 'standard'
      ? 'standard'
      : { '@initial': web, '@mobile': 'standard' };
  }

  if (mobileWeb) {
    return mobileWeb === 'standard'
      ? 'standard'
      : { '@initial': 'standard', '@mobile': mobileWeb };
  }

  return 'standard';
};

type ModalContentProps = Omit<
  MasonryShowModalMessage['payload'],
  'content' | 'alignment' | 'elementToFocusOnClose'
> & {
  node: ReactElement;
  onCloseAutoFocus: ComponentProps<typeof Modal.Content>['onCloseAutoFocus'];
};

/**
 * A wrapper around `Modal.Content` for rendering node-based modals.
 */
const ModalContent = ({
  title = '',
  headingLevel = '2',
  headingSize = 'sm',
  hideTitle,
  layout,
  showCloseButton,
  node,
  onCloseAutoFocus,
}: ModalContentProps) => {
  const strings = useThemeStrings();

  return (
    <Modal.Content
      /**
       * when the title is not hidden, we DON'T use `Modal.Content`'s built-in close icon.
       * Instead, we delegate that job to the `<HeadingBar>` component. See the Genesis
       * Core Storybook for reference.
       */
      showCloseButton={hideTitle ? showCloseButton : false}
      onCloseAutoFocus={onCloseAutoFocus}
      /**
       * When the title is not hidden, we have no choice but to move the responsibility of
       * padding the modal content AWAY from `Modal.Content` and over to a bespoke `<Box>`
       * wrapping around all non-heading elements. This is because when the title is shown,
       * the heading bar takes up the entire width of the modal. See the Genesis Core Storybook
       * for reference.
       */
      padding={hideTitle ? '$oneAndHalf' : '$none'}
      layout={buildLayoutPropValue(layout)}
    >
      {hideTitle ? (
        <Modal.Title>
          <VisuallyHidden>{title}</VisuallyHidden>
        </Modal.Title>
      ) : (
        <HeadingBar
          trailingAction={
            showCloseButton && (
              <Modal.Close>
                <Button
                  priority="tertiary"
                  quiet
                  icon="tinyClose"
                  hideLabel
                  size="small"
                >
                  {strings.close}
                </Button>
              </Modal.Close>
            )
          }
          title={
            <Modal.Title>
              <HeadingText level={headingLevel} size={headingSize}>
                {title}
              </HeadingText>
            </Modal.Title>
          }
        />
      )}
      <Box
        /**
         * When the title is not hidden, we DISABLE padding at the `Modal.Content` level
         * (above). To compensate, we add it here instead, but only on the sides and bottom,
         * since the heading bar already provides top padding.
         */
        css={{
          padding: hideTitle ? '$none' : '$none $oneAndHalf $oneAndHalf',

          /**
           * Since we're rendering this `<NodeResolver>` inside a modal,
           * and modals are portals in a separate DOM element, we need to duplicate
           * the "hidden" CSS rule that `<MasonryEngine>` wraps its DOM in for show/hide logic
           * to work properly.
           */
          [`.${HIDDEN_SUBTREE_CLASS_NAME}`]: {
            display: 'none',
          },
        }}
      >
        {node}
      </Box>
    </Modal.Content>
  );
};

type LegacyModalContentProps = Omit<
  MasonryShowModalMessage['payload'],
  'content' | 'elementToFocusOnClose'
> & {
  body?: string;
  ctaLabel?: string;
  onCloseAutoFocus: ComponentProps<typeof Modal.Content>['onCloseAutoFocus'];
};

/**
 * A wrapper around `Modal.Content` for rendering legacy text-based modals.
 */
const LegacyModalContent = ({
  title,
  headingLevel = '2',
  headingSize = 'sm',
  alignment,
  body,
  ctaLabel,
  onCloseAutoFocus,
  layout,
  hideTitle,
  showCloseButton,
}: LegacyModalContentProps) => (
  <Modal.Content
    showCloseButton={showCloseButton}
    onCloseAutoFocus={onCloseAutoFocus}
    layout={buildLayoutPropValue(layout)}
    width={{ '@initial': 384, '@mobile': '100%' }}
  >
    <StackLayout
      spacing="$oneAndHalf"
      horizontalAlignment={alignment || 'center'}
      css={{ textAlign: alignment || 'center' }}
    >
      {title && (
        <Modal.Title>
          {hideTitle ? (
            <VisuallyHidden>{title}</VisuallyHidden>
          ) : (
            <HeadingText level={headingLevel} size={headingSize}>
              {title}
            </HeadingText>
          )}
        </Modal.Title>
      )}
      {body && (
        <Modal.Description>
          <ParagraphText emphasis="subtle">{body}</ParagraphText>
        </Modal.Description>
      )}
      {ctaLabel && (
        <Modal.Close>
          <Button width="fillContainer">{ctaLabel}</Button>
        </Modal.Close>
      )}
    </StackLayout>
  </Modal.Content>
);

type InstanceModalProps = {
  renderNode: (node: MasonryEngineNode) => ReactElement;
};

/**
 * Renders a modal owned by a single Masonry instance. Designed to be
 * rendered inside the root {@link NodeResolver `NodeResolver`}, so that
 * if the content of this modal is itself a node, the latter behaves like a child
 * of the former.
 */
export const InstanceModal = memo(({ renderNode }: InstanceModalProps) => {
  const { messagingController } = useMasonryEngineConfig();
  /**
   * The modal config (or lackthereof) is stored in the state controller,
   * and is set/unset when `showModal`/`dismissModal` messages are received.
   */
  const state = useStateControllerStore(
    ({ instance: { modalState } }) => modalState,
  );

  const onModalOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        /**
         * The modal may be closing because of clicking on the scrim or pressing
         * escape. In that case, since this is a controlled modal, we need to
         * inform the masonry engine instance that the modal is being dismissed.
         *
         * If the modal is being closed programmatically by the engine itself
         * (i.e., a `dismissMessage` was received), then this will simply be a no-op.
         */
        messagingController.sendMessageToInstance({
          type: 'dismissModal',
          payload: {},
        });
      }
    },
    [messagingController],
  );

  const elementToFocusOnClose = useRef<HTMLElement>();
  if (state) {
    elementToFocusOnClose.current = state.elementToFocusOnClose;
  }

  const onCloseAutoFocus = useCallback((event: Event) => {
    event.preventDefault();

    elementToFocusOnClose.current?.focus();
  }, []);

  return (
    <Modal.Root open={!!state?.content?.kind} onOpenChange={onModalOpenChange}>
      {state &&
        (state.content.kind === 'node' ? (
          <ModalContent
            title={state.title}
            hideTitle={state.hideTitle}
            headingLevel={state.headingLevel}
            headingSize={state.headingSize}
            showCloseButton={state.showCloseButton}
            onCloseAutoFocus={onCloseAutoFocus}
            layout={state.layout}
            node={renderNode(state.content.node)}
          />
        ) : (
          <LegacyModalContent
            title={state.title}
            headingLevel={state.headingLevel}
            headingSize={state.headingSize}
            hideTitle={state.hideTitle}
            showCloseButton={state.showCloseButton}
            body={state.content.body}
            ctaLabel={state.content.ctaLabel}
            alignment={state.alignment}
            onCloseAutoFocus={onCloseAutoFocus}
          />
        ))}
    </Modal.Root>
  );
});
